import { VisualConfig, DropPosition } from '../types';

export interface DragPreviewOptions {
  element: HTMLElement;
  offset: { x: number; y: number };
  opacity?: number;
  scale?: number;
  useDomClone?: boolean;
}

export interface DropIndicatorOptions {
  position: DropPosition;
  indicatorClass?: string;
  zIndex?: number;
}

export class VisualManager {
	private config: Required<VisualConfig>;
	private dragPreview: HTMLElement | null = null;
	private dropIndicator: HTMLElement | null = null;
	private activationIndicator: HTMLElement | null = null;
  
	private previewContainer: HTMLElement;
	private indicatorContainer: HTMLElement;
  
	constructor(config?: Partial<VisualConfig>) {
		this.config = {
			previewOpacity: 0.8,
			previewScale: 1.05,
			previewZIndex: 9999,
			indicatorZIndex: 9998,
			useDomClone: true,
			...config
		};
    
		// Создаем контейнеры для визуальных элементов
		this.previewContainer = this.createContainer('dnd-preview-container', this.config.previewZIndex);
		this.indicatorContainer = this.createContainer('dnd-indicator-container', this.config.indicatorZIndex);
    
		document.body.appendChild(this.previewContainer);
		document.body.appendChild(this.indicatorContainer);
	}
  
	/**
   * Показывает визуальную обратную связь при активации
   */
	showActivationFeedback(element: HTMLElement, cssClass?: string): void {
	 if (!element) return;
		// Добавляем CSS класс элементу
		if (cssClass) {
			element.classList.add(cssClass);
		}
    
		// Создаем отдельный индикатор активации (опционально)
		this.activationIndicator = document.createElement('div');
		this.activationIndicator.className = 'dnd-activation-indicator';
		this.activationIndicator.style.position = 'absolute';
		this.activationIndicator.style.pointerEvents = 'none';
		this.activationIndicator.style.zIndex = `${this.config.indicatorZIndex - 1}`;
    
		// Позиционируем вокруг элемента
		const rect = element.getBoundingClientRect();
		this.activationIndicator.style.left = `${rect.left - 4}px`;
		this.activationIndicator.style.top = `${rect.top - 4}px`;
		this.activationIndicator.style.width = `${rect.width + 8}px`;
		this.activationIndicator.style.height = `${rect.height + 8}px`;
		this.activationIndicator.style.border = '2px solid rgba(59, 130, 246, 0.5)';
		this.activationIndicator.style.borderRadius = '8px';
		this.activationIndicator.style.animation = 'dnd-pulse 1s infinite';
    
		this.indicatorContainer.appendChild(this.activationIndicator);
	}
  
	/**
   * Создает превью для перетаскиваемого элемента
   */
	createDragPreview(element: HTMLElement, offset: { x: number; y: number }): void {
		this.clearDragPreview();
    
		const options: DragPreviewOptions = {
			element,
			offset,
			opacity: this.config.previewOpacity,
			scale: this.config.previewScale,
			useDomClone: this.config.useDomClone
		};
    
		this.dragPreview = this.config.useDomClone
			? this.createDomClonePreview(options)
			: this.createCustomPreview(options);
    
		this.previewContainer.appendChild(this.dragPreview);
	}
  
	/**
   * Обновляет позицию превью
   */
	updateDragPreview(x: number, y: number): void {
		if (!this.dragPreview) return;
    
		// Применяем трансформацию с аппаратным ускорением
		this.dragPreview.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${this.config.previewScale})`;
    
		// Синхронизируем размеры если они изменились
		requestAnimationFrame(() => {
			if (this.dragPreview && this.dragPreview.dataset.originalId) {
				const original = document.querySelector(`[data-dnd-id="${this.dragPreview.dataset.originalId}"]`);
				if (original && original instanceof HTMLElement) {
					const rect = original.getBoundingClientRect();
					this.dragPreview.style.width = `${rect.width}px`;
					this.dragPreview.style.height = `${rect.height}px`;
				}
			}
		});
	}
  
	/**
   * Создает индикатор дроп-зоны
   */
	createDropIndicator(position: DropPosition, indicatorClass?: string): void {
		this.clearDropIndicator();
    
		const options: DropIndicatorOptions = {
			position,
			indicatorClass,
			zIndex: this.config.indicatorZIndex
		};
    
		this.dropIndicator = this.createPositionedIndicator(options);
    
		if (this.dropIndicator) {
			this.indicatorContainer.appendChild(this.dropIndicator);
      
			// Анимация появления
			requestAnimationFrame(() => {
				if (this.dropIndicator) {
					this.dropIndicator.style.opacity = '1';
					this.dropIndicator.style.transform = 'scaleY(1)';
				}
			});
		}
	}
  
	/**
   * Обновляет позицию индикатора дроп-зоны
   */
	updateDropIndicator(position: DropPosition, indicatorClass?: string): void {
		if (!this.dropIndicator) {
			this.createDropIndicator(position, indicatorClass);
			return;
		}
    
		// Обновляем CSS классы
		if (indicatorClass) {
			this.dropIndicator.className = `dnd-drop-indicator ${indicatorClass}`;
		}
    
		// Обновляем позиционирование если нужно
		if (position.visual?.style) {
			Object.assign(this.dropIndicator.style, position.visual.style);
		}
    
		// Если есть элемент для привязки
		if (position.visual?.element) {
			const rect = position.visual.element.getBoundingClientRect();
			const containerRect = this.indicatorContainer.getBoundingClientRect();
      
			switch (position.type) {
			case 'between-tasks':
				this.dropIndicator.style.left = `${rect.left - containerRect.left}px`;
				this.dropIndicator.style.top = `${rect.top - containerRect.top - 2}px`;
				this.dropIndicator.style.width = `${rect.width}px`;
				this.dropIndicator.style.height = '4px';
				break;
          
			case 'empty-container':
				this.dropIndicator.style.left = `${rect.left - containerRect.left}px`;
				this.dropIndicator.style.top = `${rect.top - containerRect.top}px`;
				this.dropIndicator.style.width = `${rect.width}px`;
				this.dropIndicator.style.height = `${rect.height}px`;
				break;
			}
		}
	}
  
	/**
   * Очищает превью перетаскивания
   */
	clearDragPreview(): void {
		if (this.dragPreview) {
			// Анимация исчезновения
			this.dragPreview.style.opacity = '0';
			this.dragPreview.style.transform += ' scale(0.9)';
      
			setTimeout(() => {
				if (this.dragPreview && this.dragPreview.parentNode) {
					this.dragPreview.parentNode.removeChild(this.dragPreview);
				}
				this.dragPreview = null;
			}, 200);
		}
	}
  
	/**
   * Очищает индикатор дроп-зоны
   */
	clearDropIndicator(): void {
		if (this.dropIndicator) {
			// Анимация исчезновения
			this.dropIndicator.style.opacity = '0';
			this.dropIndicator.style.transform = 'scaleY(0.5)';
      
			setTimeout(() => {
				if (this.dropIndicator && this.dropIndicator.parentNode) {
					this.dropIndicator.parentNode.removeChild(this.dropIndicator);
				}
				this.dropIndicator = null;
			}, 150);
		}
    
		if (this.activationIndicator) {
			this.activationIndicator.remove();
			this.activationIndicator = null;
		}
	}
  
	/**
   * Очищает все визуальные элементы
   */
	clearAll(): void {
		this.clearDragPreview();
		this.clearDropIndicator();
	}
  
	/**
   * Уничтожение менеджера
   */
	destroy(): void {
		this.clearAll();
    
		if (this.previewContainer.parentNode) {
			this.previewContainer.parentNode.removeChild(this.previewContainer);
		}
    
		if (this.indicatorContainer.parentNode) {
			this.indicatorContainer.parentNode.removeChild(this.indicatorContainer);
		}
	}
  
	// ========== PRIVATE METHODS ==========
  
	private createContainer(className: string, zIndex: number): HTMLElement {
		const container = document.createElement('div');
		container.className = className;
		container.style.position = 'fixed';
		container.style.top = '0';
		container.style.left = '0';
		container.style.width = '0';
		container.style.height = '0';
		container.style.zIndex = `${zIndex}`;
		container.style.pointerEvents = 'none';
		container.style.overflow = 'visible';
    
		return container;
	}
  
	private createDomClonePreview(options: DragPreviewOptions): HTMLElement {
		const { element, offset, opacity, scale } = options;
    
		// Клонируем элемент со всеми стилями
		const clone = element.cloneNode(true) as HTMLElement;
    
		// Устанавливаем уникальный ID для связи с оригиналом
		const originalId = element.getAttribute('data-dnd-id') || 
                      `dnd-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
		if (!element.hasAttribute('data-dnd-id')) {
			element.setAttribute('data-dnd-id', originalId);
		}
    
		clone.setAttribute('data-dnd-clone-of', originalId);
		clone.setAttribute('data-dnd-preview', 'true');
    
		// Стилизация превью
		const rect = element.getBoundingClientRect();
    
		clone.style.position = 'absolute';
		clone.style.left = '0';
		clone.style.top = '0';
		clone.style.width = `${rect.width}px`;
		clone.style.height = `${rect.height}px`;
		clone.style.opacity = `${opacity}`;
		clone.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0) scale(${scale})`;
		clone.style.transformOrigin = 'top left';
		clone.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.2)';
		clone.style.borderRadius = 'inherit';
		clone.style.pointerEvents = 'none';
		clone.style.zIndex = `${this.config.previewZIndex}`;
		clone.style.transition = 'transform 0.1s, opacity 0.2s';
    
		// Сохраняем offset для точного позиционирования
		clone.dataset.offsetX = offset.x.toString();
		clone.dataset.offsetY = offset.y.toString();
		clone.dataset.originalId = originalId;
    
		// Убираем интерактивные элементы из клона
		this.disableInteractiveElements(clone);
    
		return clone;
	}
  
	private createCustomPreview(options: DragPreviewOptions): HTMLElement {
		const { element, offset, opacity, scale } = options;
    
		const preview = document.createElement('div');
		preview.className = 'dnd-custom-preview';
    
		// Копируем основные стили
		const computedStyle = window.getComputedStyle(element);
		const rect = element.getBoundingClientRect();
    
		preview.style.position = 'absolute';
		preview.style.left = '0';
		preview.style.top = '0';
		preview.style.width = `${rect.width}px`;
		preview.style.height = `${rect.height}px`;
		preview.style.backgroundColor = computedStyle.backgroundColor;
		preview.style.border = computedStyle.border;
		preview.style.borderRadius = computedStyle.borderRadius;
		preview.style.opacity = `${opacity}`;
		preview.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0) scale(${scale})`;
		preview.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.2)';
		preview.style.pointerEvents = 'none';
		preview.style.zIndex = `${this.config.previewZIndex}`;
    
		// Копируем содержимое (упрощенно)
		preview.textContent = element.textContent?.substring(0, 50) || '';
    
		return preview;
	}
  
	private createPositionedIndicator(options: DropIndicatorOptions): HTMLElement | null {
		const { position, indicatorClass, zIndex } = options;
    
		if (!position.visual) {
			// Дефолтный индикатор
			const indicator = document.createElement('div');
			indicator.className = `dnd-drop-indicator ${indicatorClass || ''}`.trim();
      
			indicator.style.position = 'absolute';
			indicator.style.left = '0';
			indicator.style.top = '0';
			indicator.style.width = '100%';
			indicator.style.height = '4px';
			indicator.style.backgroundColor = '#4dabf7';
			indicator.style.borderRadius = '2px';
			indicator.style.opacity = '0';
			indicator.style.transform = 'scaleY(0.5)';
			indicator.style.transition = 'opacity 0.15s, transform 0.15s';
			indicator.style.zIndex = `${zIndex}`;
			indicator.style.pointerEvents = 'none';
      
			return indicator;
		}
    
		// Кастомный индикатор из конфигурации
		if (position.visual.element) {
			const indicator = document.createElement('div');
			indicator.className = `dnd-drop-indicator ${indicatorClass || ''} ${position.visual.className || ''}`.trim();
      
			// Базовые стили
			indicator.style.position = 'absolute';
			indicator.style.pointerEvents = 'none';
			indicator.style.zIndex = `${zIndex}`;
			indicator.style.opacity = '0';
			indicator.style.transition = 'opacity 0.15s, transform 0.15s';
      
			// Применяем кастомные стили
			if (position.visual.style) {
				Object.assign(indicator.style, position.visual.style);
			}
      
			return indicator;
		}
    
		return null;
	}
  
	private disableInteractiveElements(element: HTMLElement): void {
		// Находим все интерактивные элементы внутри
		const interactive = element.querySelectorAll(
			'button, input, textarea, select, a, [tabindex], [contenteditable]'
		);
    
		interactive.forEach(el => {
			if (el instanceof HTMLElement) {
				el.setAttribute('disabled', 'true');
				el.setAttribute('aria-disabled', 'true');
				el.style.pointerEvents = 'none';
				el.style.opacity = '0.5';
			}
		});
	}
}

